import { FileText, MonitorUp, PenTool, PlayCircle, Users, X } from 'lucide-react'
import type { StageItem, StageItemKind } from '../hooks/useStageContent'

/**
 * The switcher for everything currently being presented.
 *
 * Shown only when there is a choice to make. With one thing on the stage the
 * bar would be a row of exactly one chip that does nothing when pressed, which
 * is worse than no bar: it takes vertical space from the content to tell the
 * viewer something the content already tells them.
 */

interface StageContentBarProps {
  items: StageItem[]
  activeKey: string | null
  onSelect: (key: string) => void
  /** Moderator only: turn the sync on, or (as its owner) off. */
  canSync: boolean
  /** This client owns the sync in force: the control shows as switched on. */
  syncActive: boolean
  onSync: () => void
  /** This client is on the host's pick and has not chosen otherwise since. */
  following: boolean
  /** Inside someone else's sync: the chip stays and offers no way out. */
  followingLocked: boolean
  onStopFollowing: () => void
  /**
   * "Sync on by <name>", for a moderator who is not the owner, or null. Takes
   * the place of the following chip for them: they need to know who is
   * driving, not just that someone is.
   */
  syncOnByLabel: string | null
  /** The host only: turns off a sync someone else owns. */
  onTurnOffSync?: () => void
  turnOffSyncLabel: string
  syncLabel: string
  /** The owner's control while its sync is on. */
  syncingLabel: string
  followingLabel: string
}

// A sync in force wears the selected tab's blue, for everyone in the room
// whichever side of it they are on: it is the room's selection, made for them.
const SYNC_ON_CLASSES = 'bg-primary text-white ring-1 ring-primary'
// The one-off follow (no sync in force) keeps its amber, so the two are told apart.
const FOLLOWING_CLASSES = 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30 hover:bg-amber-500/25'

const ICONS: Record<StageItemKind, typeof MonitorUp> = {
  screen: MonitorUp,
  whiteboard: PenTool,
  document: FileText,
  video: PlayCircle,
}

export function StageContentBar({
  items,
  activeKey,
  onSelect,
  canSync,
  syncActive,
  onSync,
  following,
  followingLocked,
  onStopFollowing,
  syncOnByLabel,
  onTurnOffSync,
  turnOffSyncLabel,
  syncLabel,
  syncingLabel,
  followingLabel,
}: StageContentBarProps) {
  if (items.length < 2) return null

  return (
    <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto shrink-0">
      {items.map((item) => {
        const Icon = ICONS[item.kind]
        const isActive = item.key === activeKey
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            aria-pressed={isActive}
            title={item.label}
            className={[
              'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium',
              'whitespace-nowrap transition-colors shrink-0 ring-1',
              isActive
                ? 'bg-primary text-white ring-primary'
                : 'bg-card/10 text-white/70 ring-white/10 hover:bg-card/20 hover:text-white',
            ].join(' ')}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {/* The label is the only thing that identifies one screen share
                from another, so it is allowed to be long and is clipped rather
                than dropped at narrow widths. */}
            <span className="max-w-[10rem] truncate">{item.label}</span>
          </button>
        )
      })}

      <div className="ml-auto flex items-center gap-2 shrink-0">
        {syncOnByLabel !== null ? (
          onTurnOffSync ? (
            <button
              type="button"
              onClick={onTurnOffSync}
              aria-pressed
              title={turnOffSyncLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-primary/90 ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{syncOnByLabel}</span>
              <X className="h-3 w-3" />
            </button>
          ) : (
            <span
              title={syncOnByLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{syncOnByLabel}</span>
            </span>
          )
        ) : (following || followingLocked) && (
          followingLocked ? (
            <span
              title={followingLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{followingLabel}</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={onStopFollowing}
              title={followingLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${FOLLOWING_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{followingLabel}</span>
              <X className="h-3 w-3" />
            </button>
          )
        )}
        {canSync && (
          <button
            type="button"
            onClick={onSync}
            aria-pressed={syncActive}
            title={syncActive ? syncingLabel : syncLabel}
            className={[
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
              syncActive
                ? `${SYNC_ON_CLASSES} hover:bg-primary/90`
                : 'bg-card/10 text-white/80 ring-1 ring-white/10 hover:bg-card/20 hover:text-white',
            ].join(' ')}
          >
            <Users className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{syncActive ? syncingLabel : syncLabel}</span>
          </button>
        )}
      </div>
    </div>
  )
}
